import PropTypes from 'prop-types';
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useTranslations } from 'next-intl';
import ReCAPTCHA from 'react-google-recaptcha';
import fetch from 'isomorphic-unfetch';

import Checkbox from '@components/atoms/checkbox/checkbox';
import Button from '../../atoms/button/button';
import Input from '../../atoms/input/input';
import Select from '../../atoms/select/select';
import Textarea from '../../atoms/textarea/textarea';
import Notification from '../../atoms/notification/notification';
import MixedCopy from '../mixed-copy/mixedCopy';

import StyledForm from './form.style';
import { fetchCsrfToken } from '../../../helper/api';

// Helper validation function moved outside the component to prevent recreation
const validateEmail = value => {
  const re =
    /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  return re.test(value);
};

const isFieldValueValid = (cell, value) => {
  if (!value) return !cell.required;
  if (cell.type === 'email') return validateEmail(value);
  return true;
};

function Field(props) {
  const { className, fieldType, ...rest } = props;

  switch (fieldType) {
    case 'input':
      return <Input className={className} {...rest} />;
    case 'select':
      return <Select className={className} {...rest} />;
    case 'textarea':
      return <Textarea className={className} {...rest} />;
    case 'checkbox':
      return <Checkbox className={className} {...rest} />;
    case 'infoText':
      return <MixedCopy html={rest.copy} className="infoText form__cell" />;
    default:
      return null;
  }
}

Field.propTypes = {
  fieldType: PropTypes.string,
  disabled: PropTypes.bool,
  className: PropTypes.string
};

const Form = ({
  id,
  withCsrfToken = false,
  cells,
  method = 'post',
  target = '_self',
  action = null,
  submitText = 'Abschicken',
  successMessage = null,
  labelWidth = null,
  onSubmit = () => {}
}) => {
  const t = useTranslations();

  // Generate initial form state based on provided cells
  const initialFormData = useMemo(() => {
    const initialData = {};
    cells.forEach(cell => {
      const error = !cell.required || (cell.options && cell.options[0].value) ? false : null;
      const value = cell.options ? cell.options[0].value : '';
      initialData[cell.name] = { value, error, active: false };
    });
    return initialData;
  }, [cells]);

  const [formData, setFormData] = useState(initialFormData);
  const [csrfToken, setCsrfToken] = useState('');
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [formError, setFormError] = useState(false);
  const [recaptchaError, setRecaptchaError] = useState(false);
  const [disabled, setDisabled] = useState(true);

  const recaptchaRef = useRef(null);
  const formRef = useRef(null);

  const warnings = useMemo(
    () => ({
      text: t('formField.text'),
      email: t('formField.email')
    }),
    [t]
  );

  useEffect(() => {
    setDisabled(false);
    if (withCsrfToken) {
      fetchCsrfToken(
        token => setCsrfToken(token),
        err => console.log(err)
      );
    }
  }, [withCsrfToken]);

  useEffect(() => {
    let timeout;
    if (formSubmitted) {
      timeout = setTimeout(() => setFormSubmitted(false), 5000);
    }
    return () => clearTimeout(timeout);
  }, [formSubmitted]);

  useEffect(() => {
    let timeout;
    if (formError) {
      timeout = setTimeout(() => setFormError(false), 5000);
    }
    return () => clearTimeout(timeout);
  }, [formError]);

  const getFormData = useCallback(
    recaptchaValue => {
      const formDataObject = {};
      Object.keys(formData).forEach(key => {
        formDataObject[key] = formData[key].value || null;
      });
      formDataObject['g-recaptcha-response'] = recaptchaValue;
      return formDataObject;
    },
    [formData]
  );

  const validateForm = useCallback(() => {
    let isFormValid = true;
    const nextFormData = { ...formData };

    cells.forEach(cell => {
      if (!cell.name || !nextFormData[cell.name]) return;

      const fieldIsValid = isFieldValueValid(cell, nextFormData[cell.name].value);
      if (!fieldIsValid) {
        isFormValid = false;
        nextFormData[cell.name] = { ...nextFormData[cell.name], error: true };
      }
    });

    setFormData(nextFormData);
    return isFormValid;
  }, [cells, formData]);

  const handleChangeField = useCallback(({ target: { name, value } }) => {
    setFormData(prev => ({
      ...prev,
      [name]: { ...prev[name], value }
    }));
  }, []);

  const handleFocusField = useCallback(({ target: { name } }) => {
    setFormData(prev => ({
      ...prev,
      [name]: {
        ...prev[name],
        active: true,
        error: prev[name].error !== false ? null : false
      }
    }));
  }, []);

  const handleBlurField = useCallback(
    ({ target: { name, value } }) => {
      const cell = cells.find(candidateCell => candidateCell.name === name);
      const fieldIsValid = cell ? isFieldValueValid(cell, value) : true;

      setFormData(prev => ({
        ...prev,
        [name]: { ...prev[name], active: false, error: !fieldIsValid, value }
      }));
    },
    [cells]
  );

  const handleSubmit = useCallback(
    e => {
      e.preventDefault();

      const check = validateForm();
      const recaptchaValue = recaptchaRef.current ? recaptchaRef.current.getValue() : null;

      if (!recaptchaValue) {
        setRecaptchaError(true);
      }

      if (check && recaptchaValue) {
        if (withCsrfToken) {
          const payload = getFormData(recaptchaValue);

          fetch(action, {
            credentials: 'include',
            method: 'POST',
            headers: {
              'X-CSRF-Token': csrfToken,
              Accept: 'application/json',
              'Content-Type': 'application/json'
            },
            body: JSON.stringify(payload)
          })
            .then(res => res.json())
            .then(json => {
              if (json.message) {
                setFormSubmitted(true);
                setCsrfToken(json.token);
              } else if (json.error) {
                setFormError(true);
                setCsrfToken(json.token);
              }
            })
            .catch(json => {
              setFormError(true);
              setCsrfToken(json?.token || '');
            });
        } else {
          onSubmit(e);
          if (formRef.current) {
            formRef.current.action = action;
            formRef.current.submit();
            formRef.current.action = '';
          }
          setFormSubmitted(true);
        }

        // Reinitialize form
        if (formRef.current) formRef.current.reset();
        if (recaptchaRef.current) recaptchaRef.current.reset();
        setFormData(initialFormData);
      }
    },
    [action, csrfToken, getFormData, initialFormData, onSubmit, validateForm, withCsrfToken]
  );

  return (
    <StyledForm
      onSubmit={handleSubmit}
      id={id}
      ref={formRef}
      method={method}
      target={target}
      role="form"
      noValidate
      labelWidth={labelWidth}
    >
      <div className="form__inner">
        {cells.map(cell => {
          const { modifier, fieldType, name, type = 'text', required } = cell;

          let cellClassName = 'form__cell';
          cellClassName = modifier
            ? `${cellClassName} ${cellClassName}--${modifier}`
            : cellClassName;

          let validation = null;
          let events = null;
          let currentValue = null;

          if (fieldType !== 'button') {
            const cellState = formData[name] || { error: null, active: false, value: '' };
            const { error, active, value } = cellState;

            events = {
              onChange: handleChangeField,
              onBlur: handleBlurField,
              onFocus: handleFocusField,
              active
            };
            currentValue = value;

            if (required || type === 'email') {
              validation = { warning: warnings[type] || warnings.text, error };
            }
          }

          return (
            <Field
              key={cell.id || name}
              className={cellClassName}
              value={currentValue}
              type={type}
              required={required} // <-- Pass this too to fix missing aria-required attributes
              {...cell}
              {...events}
              {...validation}
            />
          );
        })}

        <div className="form__cell form__recaptcha-wrapper">
          <div className={`form__recaptcha ${recaptchaError ? 'has-error' : ''}`}>
            {process.env.NEXT_PUBLIC_RECAPTCHA_SITEKEY ? (
              <ReCAPTCHA
                sitekey={process.env.NEXT_PUBLIC_RECAPTCHA_SITEKEY}
                onChange={() => setRecaptchaError(false)}
                ref={recaptchaRef}
              />
            ) : (
              console.error('RECAPCHA_SITEKEY is undefined in Form component')
            )}
            <p className="form__recaptcha-error">{t('form.recaptchaError')}</p>
          </div>
        </div>

        <div className="form__cell form__submit-button">
          <Button disabled={disabled} type="submit">
            {submitText}
          </Button>
        </div>
      </div>

      {formSubmitted && (
        <Notification type="success">{successMessage || t('form.success')}</Notification>
      )}

      {formError && <Notification type="error">{t('form.error')}</Notification>}
    </StyledForm>
  );
};

Form.propTypes = {
  id: PropTypes.string.isRequired,
  withCsrfToken: PropTypes.bool,
  cells: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  fieldType: PropTypes.string,
  method: PropTypes.string,
  target: PropTypes.string,
  action: PropTypes.string,
  submitText: PropTypes.string,
  successMessage: PropTypes.node,
  onSubmit: PropTypes.func,
  labelWidth: PropTypes.number
};

export default Form;
